<script setup lang="ts">
const documentId = inject("documentId") as string;
const referenceChecklistStore = useAssignmentReferenceChecklist(documentId)();
const { checklist, error, isLoading } = storeToRefs(referenceChecklistStore);

onMounted(() => referenceChecklistStore.initialize());

const unitLabel = computed(() => {
  const unit = String(checklist.value?.unit ?? "").trim();
  if (!unit) return "assignment";
  return unit.toLowerCase().startsWith("unit ") ? unit : `Unit ${unit}`;
});
</script>

<template>
  <div class="h-full overflow-auto p-4">
    <div v-if="isLoading" class="flex h-full items-center justify-center">
      <BaseLoading />
    </div>

    <div
      v-else-if="error"
      class="rounded border border-danger-default bg-danger-default/10 p-3 text-danger-default"
    >
      {{ error }}
      <BaseTextButton class="mt-2" @click="referenceChecklistStore.initialize()">
        Try Again
      </BaseTextButton>
    </div>

    <template v-else-if="checklist">
      <h2
        class="mb-4 border-b pb-1 text-h5 font-bold"
      >
        {{ checklist.title }}
      </h2>
      <p class="mb-4 text-left text-sm text-primary">
        Write a
        <span class="text-warning">
          {{ checklist.numParagraphs ?? 1 }}-paragraph
        </span>
        composition. Follow the
        <span class="text-warning">{{ unitLabel }}</span>
        model.<br>
        Do what you check and check what you do.
      </p>

      <DocumentStudentEditingReferenceChecklistCriterion
        v-for="(instance, index) in checklist.criteriaInstances ?? []"
        :key="`root-${index}`"
        :instance="instance"
        :instance-index="index"
        section-path="root"
      />
      <DocumentStudentEditingReferenceChecklistSection
        v-for="(section, index) in checklist.sections ?? []"
        :key="section.id ?? index"
        :section="section"
        :section-path="section.id ?? `section-${index}`"
      />
      <DocumentStudentEditingReferenceChecklistCriterion
        v-for="(instance, index) in checklist.customCriteriaInstances ?? []"
        :key="`custom-${index}`"
        :instance="instance"
        :instance-index="index"
        section-path="custom"
      />
    </template>
    <p v-else class="text-body-2 text-muted-foreground">
      No checklist is configured for this assignment. You can continue to review your paper.
    </p>
  </div>
</template>
